// Timers in progress, any number at once.
//
// A timer is a row in `running_timers` until it is stopped, when it becomes a
// `time_entries` row and the timer row goes. It used to be one localStorage
// key written in two shapes by three places, which is why only one thing could
// be timed and why the top bar and the Time Tracking page disagreed about
// pauses. Everything that reads or changes a timer now comes through here.

import { supabase } from './supabase'
import { roundUpTo15Min } from '../pages/time-tracking/helpers'

const CHANGED = 'ac-timers-changed'
const CROSS_TAB_KEY = 'ac_timers_rev'
const LEGACY_KEY = 'ac_timer'

const ms = (iso) => (iso ? new Date(iso).getTime() : NaN)

/** Seconds actually worked: wall time since the start, less every pause. */
export function elapsedSeconds(timer, nowMs = Date.now()) {
  const start = ms(timer?.started_at)
  if (!Number.isFinite(start)) return 0
  const pausedNow = timer.paused_since ? Math.max(0, nowMs - ms(timer.paused_since)) : 0
  const total = nowMs - start - (Number(timer.paused_seconds) || 0) * 1000 - pausedNow
  return Math.max(0, Math.floor(total / 1000))
}

export const isPaused = (timer) => !!timer?.paused_since

/**
 * What the time is worth if stopped now: the 15-minute rounding the entry will
 * be saved with, times the timer's rate. Null when there is no rate, so the
 * screen can say "no rate" rather than "$0.00".
 */
export function billedAmount(timer, nowMs = Date.now()) {
  const rate = Number(timer?.hourly_rate)
  if (!timer?.hourly_rate || !Number.isFinite(rate)) return null
  return Math.round((roundUpTo15Min(elapsedSeconds(timer, nowMs)) / 3600) * rate * 100) / 100
}

/** h:mm:ss, or m:ss under an hour. */
export function clock(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

/**
 * The time_entries row a stopped timer becomes.
 *
 * A contractor's timer becomes a contractor's entry: their hours are a cost to
 * the studio, so it carries no client rate and is never billable from their
 * side — the database enforces the same, this just doesn't ask for more. The
 * key is left off entirely for the studio's own timers, so its writes do not
 * depend on the contractor_id column existing.
 */
export function entryFromTimer(timer, endedIso = new Date().toISOString()) {
  const entry = {
    client_id: timer.client_id || null,
    project_id: timer.project_id || null,
    project_name: timer.project_name || null,
    description: timer.description || null,
    category: timer.category || null,
    hourly_rate: timer.hourly_rate === null || timer.hourly_rate === undefined || timer.hourly_rate === ''
      ? null : Number(timer.hourly_rate),
    billable: timer.billable !== false,
    started_at: timer.started_at,
    ended_at: endedIso,
    duration_seconds: roundUpTo15Min(elapsedSeconds(timer, ms(endedIso))),
  }
  if (timer.contractor_id) {
    entry.contractor_id = timer.contractor_id
    entry.hourly_rate = null
    entry.billable = false
  }
  return entry
}

/**
 * What to call a timer. A contractor cannot read `clients` or `projects`, so
 * their embeds come back null; the names stored on the row stand in.
 */
export function timerNames(timer) {
  return {
    project: timer?.projects?.title || timer?.project_name || null,
    client: timer?.clients?.name || timer?.client_name || null,
  }
}

/**
 * A timer left in localStorage by the old code, as a row for the new table.
 *
 * Two shapes are out there. The Time Tracking page wrote `pauses` (finished
 * intervals) and `paused_since`; the top bar and the project cards wrote
 * `accumulated_seconds` with `paused`, nulling `started_at` while paused and
 * resetting it on resume. Either way the worked time is preserved; the start
 * is moved back far enough to account for it. Null when there is nothing
 * worth carrying over.
 */
export function timerFromLegacy(saved, nowMs = Date.now()) {
  if (!saved || typeof saved !== 'object') return null
  const base = {
    client_id: saved.client_id || null,
    project_id: saved.project_id || null,
    project_name: saved.project_name || null,
    description: saved.description || null,
    category: saved.category || null,
    hourly_rate: saved.hourly_rate ? Number(saved.hourly_rate) : null,
    billable: saved.billable !== false,
  }

  if (Array.isArray(saved.pauses) || saved.paused_since) {
    if (!saved.started_at) return null
    const pausedMs = (saved.pauses || []).reduce((sum, p) =>
      p?.paused_at && p?.resumed_at ? sum + Math.max(0, ms(p.resumed_at) - ms(p.paused_at)) : sum, 0)
    return {
      ...base,
      started_at: saved.started_at,
      paused_seconds: Math.floor(pausedMs / 1000),
      paused_since: saved.paused_since || null,
    }
  }

  const acc = Number(saved.accumulated_seconds) || 0
  const runningFor = saved.started_at && !saved.paused ? Math.max(0, nowMs - ms(saved.started_at)) : 0
  const worked = acc * 1000 + runningFor
  if (!worked && !saved.started_at) return null
  return {
    ...base,
    started_at: new Date(nowMs - worked).toISOString(),
    paused_seconds: 0,
    paused_since: saved.paused ? new Date(nowMs).toISOString() : null,
  }
}

// ── Telling every open view that something changed ─────────────────────────
// The top bar and the Time Tracking page both show timers. Same tab: a window
// event. Other tabs: a localStorage bump, which fires `storage` there. Other
// devices pick it up when the tab comes back into view.

export function announce() {
  try { window.dispatchEvent(new Event(CHANGED)) } catch {}
  try { localStorage.setItem(CROSS_TAB_KEY, String(Date.now())) } catch {}
}

export function onTimersChanged(fn) {
  const onStorage = (e) => { if (e.key === CROSS_TAB_KEY) fn() }
  window.addEventListener(CHANGED, fn)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(CHANGED, fn)
    window.removeEventListener('storage', onStorage)
  }
}

// ── Database ───────────────────────────────────────────────────────────────

const SELECT = '*, clients(name), projects(title)'
// A contractor's timers: no embeds, they would only come back null.
const CONTRACTOR_SELECT = '*'

const missingContractorColumn = (error) =>
  !!error && (error.code === '42703' || error.code === 'PGRST204' || /contractor_id/.test(error.message || ''))

/**
 * The timers this person should see. A contractor gets their own. The studio
 * gets its own too — contractors' timers live in the same table, and they are
 * not the studio's to stop or bill — falling back to every row while the
 * column that tells them apart has not been added yet.
 */
export async function loadTimers({ contractorId } = {}) {
  if (contractorId) {
    const { data, error } = await supabase.from('running_timers').select(CONTRACTOR_SELECT)
      .eq('contractor_id', contractorId).order('started_at')
    if (error) throw error
    return data || []
  }
  await adoptLegacyTimer()
  const { data, error } = await supabase.from('running_timers').select(SELECT)
    .is('contractor_id', null).order('started_at')
  if (!error) return data || []
  if (!missingContractorColumn(error)) throw error
  const retry = await supabase.from('running_timers').select(SELECT).order('started_at')
  if (retry.error) throw retry.error
  return retry.data || []
}

let adopting = null
/** Move an old localStorage timer into the table once, so no time is lost. */
async function adoptLegacyTimer() {
  if (adopting) return adopting
  adopting = (async () => {
    let saved = null
    try { saved = JSON.parse(localStorage.getItem(LEGACY_KEY) || 'null') } catch {}
    const row = timerFromLegacy(saved)
    if (!row) { try { localStorage.removeItem(LEGACY_KEY) } catch {} ; return }
    const { error } = await supabase.from('running_timers').insert(row)
    // Only forget the old timer once the new one exists.
    if (!error) { try { localStorage.removeItem(LEGACY_KEY) } catch {} }
  })().finally(() => { adopting = null })
  return adopting
}

export async function startTimer(fields, { pauseOthers = [] } = {}) {
  const now = new Date().toISOString()
  if (pauseOthers.length) {
    await Promise.all(pauseOthers.filter(t => !isPaused(t)).map(t =>
      supabase.from('running_timers').update({ paused_since: now }).eq('id', t.id)))
  }
  const row = { ...fields, started_at: now, paused_seconds: 0, paused_since: null }
  if (row.contractor_id) { row.hourly_rate = null; row.billable = false } else delete row.contractor_id
  const { data, error } = await supabase.from('running_timers')
    .insert(row)
    .select(row.contractor_id ? CONTRACTOR_SELECT : SELECT).single()
  if (error) throw error
  announce()
  return data
}

export async function pauseTimer(timer) {
  if (isPaused(timer)) return
  const { error } = await supabase.from('running_timers')
    .update({ paused_since: new Date().toISOString() }).eq('id', timer.id)
  if (error) throw error
  announce()
}

export async function resumeTimer(timer) {
  if (!isPaused(timer)) return
  const pausedFor = Math.max(0, Math.floor((Date.now() - ms(timer.paused_since)) / 1000))
  const { error } = await supabase.from('running_timers').update({
    paused_seconds: (Number(timer.paused_seconds) || 0) + pausedFor,
    paused_since: null,
  }).eq('id', timer.id)
  if (error) throw error
  announce()
}

export async function updateTimer(id, patch) {
  const { error } = await supabase.from('running_timers').update(patch).eq('id', id)
  if (error) throw error
  announce()
}

/**
 * Save the time, then drop the timer. In that order: if the entry cannot be
 * written the timer is still there to try again, rather than the hours gone.
 */
export async function stopTimer(timer) {
  const entry = entryFromTimer(timer)
  if (timer.contractor_id) {
    // A contractor may write their entry but not read the row back — once the
    // studio bills it, it carries the client's rate. So no `.select()` here.
    const { error } = await supabase.from('time_entries').insert(entry)
    if (error) throw error
    await supabase.from('running_timers').delete().eq('id', timer.id)
    announce()
    return entry
  }
  const { data, error } = await supabase.from('time_entries').insert(entry).select().single()
  if (error) throw error
  await supabase.from('running_timers').delete().eq('id', timer.id)
  announce()
  return data
}

export async function discardTimer(timer) {
  const { error } = await supabase.from('running_timers').delete().eq('id', timer.id)
  if (error) throw error
  announce()
}

/**
 * The rate to start a timer at: the client's rate if one is set, otherwise the
 * last rate used on this project, otherwise on this client. Most clients have
 * no rate on file, and retyping the same number every time is how $0 entries
 * happen.
 */
export async function suggestedRate(client, projectId) {
  if (client?.hourly_rate) return Number(client.hourly_rate)
  if (!client?.id) return null
  const { data } = await supabase.from('time_entries').select('hourly_rate, project_id')
    .eq('client_id', client.id).not('hourly_rate', 'is', null)
    .order('started_at', { ascending: false }).limit(20)
  const rows = data || []
  const onProject = projectId && rows.find(r => r.project_id === projectId)
  const pick = onProject || rows[0]
  return pick ? Number(pick.hourly_rate) : null
}
